import { useState } from "react"
import {
    Box,
    Chip,
    Divider,
    IconButton,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Popover,
    Tooltip,
    Typography,
} from "@mui/material"
import { ListItemBox } from "lib/@components/ListItemBox"
import { Repeat } from "lib/@components/repeat"
import { If } from "lib/@components/switch"
import { Else } from "lib/@components/else"
import Iconify from "minimals-template/components/Iconify"
import { skillMapTradeAssignmentCount } from "routes/skill-mapper/controller/skill-map-trade-assignment-count"
import { SkillChip } from "./skill-chip"
import { confirmAndRemoveTrade } from "./confirm-and-remove-trade"
import { TradeNoSkillsLabel } from "./trade-no-skills-label"
import { SkillMapTradeRowExtra } from "./skill-map-view-slot-definitions"

const MAX_VISIBLE_SKILLS = 3

export function SkillMapTradeRow({ item, skillMapId, readOnly, onEditMapping }) {
    const { tradeName, tradeId, skills, conditionalRuleCount, rulesRemovedSkillCount } = item
    const visibleSkills = skills.slice(0, MAX_VISIBLE_SKILLS)
    const overflowCount = skills.length - MAX_VISIBLE_SKILLS
    const [menuAnchor, setMenuAnchor] = useState(null)
    const [popoverAnchor, setPopoverAnchor] = useState(null)
    // Plugged extras (e.g. FIQ-2457's assignment summary) take over the "No skills" label
    const hasRowExtras = SkillMapTradeRowExtra.useItems().length > 0

    return (
        <>
            <ListItemButton
                data-cy="skill-map-trade-row"
                sx={{ px: 2, py: 1.5 }}
                onClick={readOnly ? undefined : handleRowClick}
                disabled={readOnly}
            >
                <ListItemBox sx={{ gap: 2, width: 1 }}>
                    <Box sx={{ width: 200, flexShrink: 0 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <Typography variant="body2" fontWeight={500} sx={{ fontSize: 16 }}>
                                {tradeName}
                            </Typography>
                            <If value={item.hasConditionalRouting}>
                                <Tooltip title="Conditional routing">
                                    <Box
                                        component="span"
                                        aria-label="Conditional routing"
                                        data-cy="trade-conditional-routing-marker"
                                        sx={{ display: "inline-flex", color: "primary.main" }}
                                    >
                                        <Iconify icon="mdi:source-branch" width={16} />
                                    </Box>
                                </Tooltip>
                            </If>
                        </Box>
                    </Box>
                    <Box sx={{ flex: 1, display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
                        <Repeat list={visibleSkills} item={<SkillChip />} />
                        <If value={hasRowExtras}>
                            <SkillMapTradeRowExtra.Slot />
                            <Else>
                                {/* Skill-less trades are listed whatever the flags (one may have
                                    been made while schedule assignments were on), so they still
                                    say why the row is empty */}
                                <If value={!skills.length}>
                                    <TradeNoSkillsLabel conditionalRuleCount={conditionalRuleCount} />
                                </If>
                            </Else>
                        </If>
                        <If value={overflowCount > 0}>
                            <Chip
                                label={`+ ${overflowCount} more`}
                                size="small"
                                onClick={openPopover}
                                sx={{
                                    bgcolor: "primary.lighter",
                                    color: "primary.main",
                                    fontWeight: 500,
                                    cursor: "pointer",
                                }}
                            />
                            <Popover
                                open={Boolean(popoverAnchor)}
                                anchorEl={popoverAnchor}
                                onClose={closePopover}
                                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                            >
                                <Box sx={{ p: 2, maxWidth: 400 }}>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                        {overflowCount} More Skills
                                    </Typography>
                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                        <Repeat list={skills.slice(MAX_VISIBLE_SKILLS)} item={<SkillChip />} />
                                    </Box>
                                </Box>
                            </Popover>
                        </If>
                    </Box>
                    <If value={!readOnly}>
                        <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                            <IconButton aria-label="Edit mapping" size="small" onClick={handleEditMapping}>
                                <Iconify icon="solar:pen-bold" width={18} />
                            </IconButton>
                            <IconButton
                                aria-label="More options"
                                size="small"
                                onClick={openMenu}
                                data-cy="skill-map-trade-row-menu"
                            >
                                <Iconify icon="eva:more-vertical-fill" width={18} />
                            </IconButton>
                            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
                                <MenuItem onClick={handleEditMapping}>
                                    <ListItemIcon>
                                        <Iconify icon="solar:pen-bold" width={20} />
                                    </ListItemIcon>
                                    <ListItemText>Edit Mapping</ListItemText>
                                </MenuItem>
                                <MenuItem
                                    onClick={handleDeleteTrade}
                                    sx={{ color: "error.main" }}
                                    data-cy="skill-map-trade-row-delete"
                                >
                                    <ListItemIcon sx={{ color: "error.main" }}>
                                        <Iconify icon="solar:trash-bin-trash-bold" width={20} />
                                    </ListItemIcon>
                                    <ListItemText>Delete</ListItemText>
                                </MenuItem>
                            </Menu>
                        </Box>
                    </If>
                </ListItemBox>
            </ListItemButton>
            <Divider />
        </>
    )

    function handleRowClick() {
        onEditMapping?.({ tradeId, tradeName, skills })
    }

    function openMenu(e) {
        e.stopPropagation()
        setMenuAnchor(e.currentTarget)
    }

    function closeMenu() {
        setMenuAnchor(null)
    }

    function openPopover(e) {
        e.stopPropagation()
        setPopoverAnchor(e.currentTarget)
    }

    function closePopover(e) {
        if (e) e.stopPropagation()
        setPopoverAnchor(null)
    }

    function handleEditMapping(e) {
        e.stopPropagation()
        closeMenu()
        onEditMapping?.({ tradeId, tradeName, skills })
    }

    async function handleDeleteTrade(e) {
        e.stopPropagation()
        closeMenu()
        const assignmentCount = await countScheduleAssignments()
        await confirmAndRemoveTrade({
            skillMapId,
            tradeId,
            tradeName,
            skillCount: skills.length,
            assignmentCount,
            conditionalRuleCount: rulesRemovedSkillCount,
        })
    }

    // FIQ-2457: removing a trade also removes the schedule assignments routed to it. If they
    // can't be counted the confirmation still opens (null), with a general warning instead.
    // Read whatever the flag: they are removed either way, and a user without the flag may
    // still have some from while it was on
    async function countScheduleAssignments() {
        try {
            return await skillMapTradeAssignmentCount({ skillMapId, tradeId })
        } catch {
            return null
        }
    }
}
